import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchViewApi from '../../../api/SearchViewApi';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import DataApi from '../../../api/DataApi';
import { effectiveFilterBindingOperator } from '../../../utils/searchFilterOperators';

export function extractPrimitiveValue(rawValue) {
  if (rawValue === null || rawValue === undefined) return null;
  if (typeof rawValue !== 'object' || Array.isArray(rawValue)) return rawValue;
  if (rawValue.value !== undefined) {
    const extracted = extractPrimitiveValue(rawValue.value);
    if (extracted !== null) return extracted;
  }
  if (rawValue.displayValue !== undefined) {
    const extracted = extractPrimitiveValue(rawValue.displayValue);
    if (extracted !== null) return extracted;
  }
  if (rawValue.name !== undefined) {
    const extracted = extractPrimitiveValue(rawValue.name);
    if (extracted !== null) return extracted;
  }
  try {
    const stringValue = String(rawValue);
    return stringValue === '[object Object]' ? null : stringValue;
  } catch {
    return null;
  }
}

/**
 * Snapshot for restoring a search tab without re-fetching (RelatedTabsModal tab cache).
 * @typedef {object} SearchViewWidgetSnapshot
 * @property {object} searchViewConfig
 * @property {Array} records
 * @property {object} fieldDataTypes
 * @property {object} fieldMetadata
 * @property {string} [searchObjectLabel]
 * @property {string} [error]
 */

/**
 * Shared data loading for search-view widgets (card or table UI).
 * @param {object} [options]
 * @param {SearchViewWidgetSnapshot|null} [options.restoredSnapshot] - hydrate from cache (skip network)
 * @param {(snap: SearchViewWidgetSnapshot) => void} [options.onSnapshotCommit] - after successful load, for parent cache
 */
export function useSearchViewWidgetData(widget, recordData, options = {}) {
  const { restoredSnapshot = null, onSnapshotCommit } = options;
  const navigate = useNavigate();
  const hydratedFromCacheRef = useRef(false);
  const onSnapshotCommitRef = useRef(onSnapshotCommit);
  onSnapshotCommitRef.current = onSnapshotCommit;
  const [searchViewConfig, setSearchViewConfig] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldDataTypes, setFieldDataTypes] = useState({});
  const [fieldMetadata, setFieldMetadata] = useState({});
  const [searchObjectLabel, setSearchObjectLabel] = useState('');
  const abortControllerRef = useRef(null);

  const widgetConfig = widget?.config || {};
  const searchViewId = widgetConfig.search_view_id;
  const filterBindings = widgetConfig.filter_bindings || [];
  const selectedFields = widgetConfig.fields || [];
  const maxRecords = widgetConfig.max_records || 10;

  const searchObjectName = searchViewConfig?.object_name;

  const getFieldValue = useCallback((record, fieldName) => {
    if (!record || !fieldName) return null;

    const normalizedFieldName = String(fieldName).toLowerCase();

    if (Array.isArray(record.highlightSection)) {
      const highlightField = record.highlightSection.find(
        (f) => f?.fieldName?.toLowerCase() === normalizedFieldName
      );
      if (highlightField && highlightField.value !== null && highlightField.value !== undefined) {
        return highlightField.value;
      }
    }

    if (Array.isArray(record.details)) {
      for (const section of record.details) {
        if (Array.isArray(section.fields)) {
          const detailField = section.fields.find(
            (f) => f?.fieldName?.toLowerCase() === normalizedFieldName
          );
          if (detailField && detailField.value !== null && detailField.value !== undefined) {
            return detailField.value;
          }
        }
      }
    }

    if (Array.isArray(record.otherHiddenFields)) {
      const hiddenField = record.otherHiddenFields.find(
        (f) => f?.fieldName?.toLowerCase() === normalizedFieldName
      );
      if (hiddenField && hiddenField.value !== null && hiddenField.value !== undefined) {
        return hiddenField.value;
      }
    }

    if (record[fieldName] !== undefined) {
      const value = record[fieldName];
      if (value && typeof value === 'object' && value.id) {
        return value.id;
      }
      return value;
    }

    return null;
  }, []);

  const fetchSearchViewConfig = useCallback(async () => {
    if (!searchViewId) return;

    try {
      const searchViewApi = new SearchViewApi();
      const response = await searchViewApi.fetchById(searchViewId);
      if (response?.data?.success && response.data.data) {
        const config = response.data.data;
        setSearchViewConfig(config);
        if (config.object_name) {
          try {
            const objectConfigResponse = await ObjectConfigApi.fetchConfigObjectByNameWithFields(
              config.object_name
            );
            const result = objectConfigResponse.data;
            if (result?.object_label) {
              setSearchObjectLabel(result.object_label);
            }
            if (result?.fields?.length > 0) {
              const dataTypes = {};
              const metadata = {};
              result.fields.forEach((field) => {
                const fieldName = field.field_name;
                dataTypes[fieldName] = field.data_type || 'text';
                metadata[fieldName] = {
                  data_type: field.data_type || 'text',
                  picklist_values: field.picklist_values_data || field.picklist_values || [],
                  reference_object: field.reference_object,
                  display_fields: field.display_fields,
                  lookup_search_fields: field.lookup_search_fields || 'name',
                  field_label: field.field_label || fieldName
                };
              });
              setFieldDataTypes(dataTypes);
              setFieldMetadata(metadata);
            }
          } catch (err) {
            console.error('Error fetching object config:', err);
          }
        }
      } else {
        setError('Search view not found.');
      }
    } catch (err) {
      console.error('Error fetching search view config:', err);
      setError(err.response?.data?.message || 'Failed to load search view configuration');
    }
  }, [searchViewId]);

  const executeSearch = useCallback(async () => {
    if (!searchViewConfig || !searchObjectName || !recordData) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    setError('');

    try {
      const dataApi = new DataApi(`object/list/${searchObjectName}`);
      const queryParams = new URLSearchParams();

      let displayFieldsArray = [];
      if (Array.isArray(searchViewConfig.display_fields)) {
        displayFieldsArray = searchViewConfig.display_fields;
      } else if (typeof searchViewConfig.display_fields === 'string') {
        try {
          displayFieldsArray = JSON.parse(searchViewConfig.display_fields);
        } catch (e) {
          displayFieldsArray = searchViewConfig.display_fields
            .split(',')
            .map((f) => f.trim())
            .filter(Boolean);
        }
      }

      const fieldsToFetch =
        selectedFields.length > 0 ? selectedFields : displayFieldsArray;

      const fieldsWithId = fieldsToFetch.includes('id') ? fieldsToFetch : ['id', ...fieldsToFetch];

      if (fieldsWithId.length > 0) {
        queryParams.append('fields', fieldsWithId.join(','));
      }

      if (filterBindings.length > 0) {
        let filterIndex = 1;
        filterBindings.forEach((binding) => {
          const { filterable_field, source_field } = binding;
          const operator = effectiveFilterBindingOperator(binding);

          if (!filterable_field) return;

          if (operator === 'is_null' || operator === 'is_not_null') {
            queryParams.append(`filter${filterIndex}`, `${filterable_field}:${operator}:`);
            filterIndex++;
            return;
          }

          if (!source_field) return;

          const sourceValue = getFieldValue(recordData, source_field);
          if (sourceValue === null || sourceValue === undefined || sourceValue === '') {
            return;
          }

          const primitive = extractPrimitiveValue(sourceValue);
          const valueToSend =
            primitive !== null && primitive !== undefined ? primitive : sourceValue;

          queryParams.append(
            `filter${filterIndex}`,
            `${filterable_field}:${operator}:${valueToSend}`
          );
          filterIndex++;
        });

        if (filterIndex > 1) {
          queryParams.append('logic', 'AND');
        }
      }

      if (searchViewConfig.sort_by) {
        queryParams.append('sortBy', searchViewConfig.sort_by);
        queryParams.append('sortOrder', searchViewConfig.sort_order || 'DESC');
      }

      queryParams.append('limit', String(maxRecords));

      const response = await dataApi.fetchAll(queryParams.toString(), {
        signal: abortController.signal
      });

      if (abortController.signal.aborted) return;

      if (response?.data?.success) {
        const nextRecords = response.data.data || [];
        setRecords(nextRecords);
        if (searchViewConfig) {
          onSnapshotCommitRef.current?.({
            searchViewConfig,
            records: nextRecords,
            fieldDataTypes,
            fieldMetadata,
            searchObjectLabel,
            error: ''
          });
        }
      } else {
        setError(response?.data?.message || 'Failed to fetch records');
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        return;
      }
      console.error('Error fetching records:', err);
      setError(err.response?.data?.message || 'Failed to fetch records');
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [
    searchViewConfig,
    searchObjectName,
    recordData,
    filterBindings,
    selectedFields,
    maxRecords,
    getFieldValue
  ]);

  const buildSearchAllPath = useCallback(() => {
    if (!searchObjectName) return '';
    const params = new URLSearchParams();
    let filterIndex = 1;
    filterBindings.forEach((binding) => {
      const op = effectiveFilterBindingOperator(binding);
      if (!binding.filterable_field) return;
      if (op === 'is_null' || op === 'is_not_null') {
        params.append(`filter${filterIndex}`, `${binding.filterable_field}:${op}:`);
        filterIndex++;
        return;
      }
      if (!binding.source_field) return;
      const sourceValue = getFieldValue(recordData, binding.source_field);
      if (sourceValue !== null && sourceValue !== undefined && sourceValue !== '') {
        const primitive = extractPrimitiveValue(sourceValue);
        const valueToSend =
          primitive !== null && primitive !== undefined ? primitive : sourceValue;
        params.append(
          `filter${filterIndex}`,
          `${binding.filterable_field}:${op}:${valueToSend}`
        );
        filterIndex++;
      }
    });
    if (filterIndex > 1) {
      params.append('logic', 'AND');
    }
    const slug = searchViewConfig?.search_view_name || widgetConfig.search_view_name;
    const basePath = slug
      ? `/search/${searchObjectName}/${encodeURIComponent(slug)}`
      : `/search/${searchObjectName}`;
    const qs = params.toString();
    return qs ? `${basePath}?${qs}` : basePath;
  }, [
    filterBindings,
    recordData,
    getFieldValue,
    searchViewConfig,
    widgetConfig.search_view_name,
    searchObjectName
  ]);

  const handleViewAll = useCallback(() => {
    const path = buildSearchAllPath();
    if (path) navigate(path);
  }, [buildSearchAllPath, navigate]);

  useEffect(() => {
    if (restoredSnapshot?.searchViewConfig) {
      hydratedFromCacheRef.current = true;
      setSearchViewConfig(restoredSnapshot.searchViewConfig);
      setRecords(restoredSnapshot.records ?? []);
      setFieldDataTypes(restoredSnapshot.fieldDataTypes ?? {});
      setFieldMetadata(restoredSnapshot.fieldMetadata ?? {});
      setSearchObjectLabel(restoredSnapshot.searchObjectLabel ?? '');
      setError(restoredSnapshot.error ?? '');
      setLoading(false);
    } else {
      hydratedFromCacheRef.current = false;
    }
  }, [restoredSnapshot]);

  useEffect(() => {
    if (hydratedFromCacheRef.current) return;
    if (searchViewId) {
      fetchSearchViewConfig();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchViewId, fetchSearchViewConfig]);

  useEffect(() => {
    if (hydratedFromCacheRef.current) return;
    if (searchViewConfig && recordData && searchObjectName && Object.keys(fieldDataTypes).length > 0) {
      executeSearch();
    }
  }, [searchViewConfig, recordData, filterBindings, fieldDataTypes, searchObjectName, executeSearch]);

  return {
    searchViewConfig,
    records,
    loading,
    error,
    fieldDataTypes,
    fieldMetadata,
    searchObjectName,
    searchObjectLabel,
    widgetConfig,
    selectedFields,
    maxRecords,
    executeSearch,
    handleViewAll,
    buildSearchAllPath,
    getFieldValue
  };
}
